/**
 * Stone Investment Locations Grid
 * 
 * Location cards display with Stone Investment's luxury aesthetic:
 * - Editorial card layout
 * - Hover effects with HorizonOverlay
 * - Links to region pages
 */

'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import type { LocationsGridProps } from '@routeroyal/theme-core';
import { detectRegion } from '../utils/regionColors';
import { HorizonOverlay } from './PropertyCard';

/**
 * Individual location card
 */
function LocationCard({ 
  location, 
  style 
}: { 
  location: LocationsGridProps['locations'][0]; 
  style: 'cards' | 'simple';
}) {
  const [isHovered, setIsHovered] = useState(false);
  const region = detectRegion(location.name);
  const isFrance = region === 'provence';
  
  const content = (
    <div
      className="group block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {style === 'cards' && location.image && (
        <div className="relative aspect-[4/3] overflow-hidden bg-stone-200 mb-6">
          <Image
            src={location.image}
            alt={location.name}
            fill
            className={`object-cover transition-transform duration-[2000ms] ease-out ${
              isHovered ? 'scale-105' : 'scale-100'
            }`}
            sizes="(max-width: 768px) 100vw, 50vw"
          />
          
          {/* HorizonOverlay */}
          <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />
          
          {/* Arrow indicator */}
          <div className={`absolute top-4 right-4 transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}>
            <ArrowUpRight size={20} className="text-white" strokeWidth={1.5} />
          </div>
        </div>
      )}
      
      <div className={style === 'simple' ? 'py-6 border-b border-stone-200' : ''}>
        <h3 
          className={`font-serif text-2xl lg:text-3xl text-stone-900 mb-2 transition-all duration-500 ${
            isHovered ? 'italic' : ''
          }`}
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {location.name}
        </h3>
        
        {location.description && (
          <p 
            className="text-sm text-stone-600 font-sans"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {location.description}
          </p>
        )}
      </div>
    </div>
  );
  
  if (location.link) {
    return (
      <Link href={location.link} className="block">
        {content}
      </Link>
    );
  }
  
  return content;
}

/**
 * StoneLocationsGrid - Location cards display
 */
export function StoneLocationsGrid({
  title,
  subtitle,
  locations,
  style = 'cards'
}: LocationsGridProps) {
  return (
    <section className="py-20 lg:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 max-w-2xl">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-4">
            Our Markets
          </p>
          <h2 
            className="font-serif text-4xl lg:text-5xl text-stone-900 mb-6"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {title}
          </h2>
          
          {subtitle && (
            <p 
              className="text-lg text-stone-600 font-sans"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {subtitle}
            </p>
          )}
        </div>
        
        {/* Locations Grid */}
        <div className={`grid ${
          style === 'cards' 
            ? 'grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16' 
            : 'grid-cols-1 gap-0'
        }`}>
          {locations.map((location, index) => (
            <LocationCard key={index} location={location} style={style} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default StoneLocationsGrid;
